
import { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";

export function UserNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const { t } = useTranslations("student");
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;

      try {
        console.log("Fetching student profile for auth_id:", user.id);
        
        // REVERTIDO: Primeiro tentar por auth_id, depois por email
        let data = null;
        let error = null;

        // Tentar primeiro por auth_id (estudantes registados)
        const { data: authIdResult, error: authIdError } = await supabase
          .from("students")
          .select("first_name, last_name, avatar_url")
          .eq("auth_id", user.id)
          .eq("status", "active")
          .maybeSingle();

        if (authIdResult) {
          data = authIdResult;
        } else if (user.email) {
          // Se não encontrou por auth_id, tentar por email (estudantes manuais)
          const { data: emailResult, error: emailError } = await supabase
            .from("students")
            .select("first_name, last_name, avatar_url")
            .eq("email", user.email)
            .eq("status", "active")
            .maybeSingle();
            
          data = emailResult;
          error = emailError;
        } else {
          error = authIdError;
        }

        if (error) {
          console.error("Error fetching profile:", error);
          return;
        }

        if (data) {
          console.log("Student data fetched:", data);
          setAvatarUrl(data.avatar_url);
          setFirstName(data.first_name);
          setLastName(data.last_name);
        }
      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchProfile();
  }, [user]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    // Redirect to landing page instead of auth page
    navigate(getPath("/")); 
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={avatarUrl || undefined} alt={`${firstName} ${lastName}`} />
            <AvatarFallback>
              {firstName?.[0]}{lastName?.[0]}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" forceMount className="w-56 bg-slate-50 z-[100]">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{`${firstName} ${lastName}`}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          {t("header.userMenu.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
